<template>
  <dir v-resize="onResize" style="padding: 0; margin: 0">
    <div v-if="hasAlternative" style="border-bottom: 1px solid #eee; margin-bottom: 15px">
      <v-layout class="mb-2">
        <v-flex class="pl-3">
          {{ $t('Chatbot answers') }}
        </v-flex>
        <v-flex shrink class="text-xs-right">
          <v-dialog v-model="showSetting" max-width="800" persistent>
            <v-card>
              <div class="text-xs-right">
                <v-btn small fab flat @click="showSetting = false"><v-icon>close</v-icon></v-btn>
              </div>
              <v-card-text>
                <strong>{{ $t('How to set random chatbot responses?') }}</strong>
                <ul>
                  <li>{{ $t('1. Press the “Random Answers” ​​button to turn green.') }}</li>
                  <li>
                    {{
                      $t(
                        '2. Choose 1-5 random answer patterns and put the desired answer in each format.^^'
                      )
                    }}
                  </li>
                </ul>

                <div class="primary--text mt-3">
                  {{ $t('Random answer') }}
                </div>
                <label
                  class="toggle-switch small"
                  style="margin-top: 10px; margin-bottom: 10px; vertical-align: bottom"
                >
                  <input
                    type="checkbox"
                    :checked="input.randomStatus"
                    @change="toggleRandomStatus()"
                  />
                  <span class="toggle-slider lever round"></span>
                </label>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn color="primary" @click="changeSetting">
                  {{ $t('Save') }}
                </v-btn>
                <v-spacer />
              </v-card-actions>
            </v-card>
          </v-dialog>

          <template v-if="!isBeacon">
            <v-btn
              v-if="!hideSetting"
              class="ma-0 extra-small"
              fab
              flat
              small
              color="grey"
              @click="showSetting = true"
            >
              <v-icon>settings</v-icon>
            </v-btn>
          </template>
        </v-flex>
      </v-layout>
    </div>
    <div ref="randomBar"></div>
    <template v-if="input.randomStatus">
      <!-- Random Intent -->
      <template v-if="isBeacon">
        <v-layout class="primary lighten-4">
          <v-flex>
            <v-tabs
              v-model="currentAnswerGroup"
              :show-arrows="true"
              color="primary lighten-4"
              slider-color="primary"
              :style="{ 'max-width': maxRandomBarWidth + 'px' }"
              @change="handleChangeAnswerGroup"
            >
              <v-tab v-for="n in alternativeGroup.length" :key="n" ripple>
                {{ $t('beacon_answer_group') }} {{ n }}
                <v-btn
                  :disabled="alternativeGroup.length == 1"
                  fab
                  flat
                  small
                  class="mr-0"
                  style="width: 25px; height: 25px"
                  @click="onDeleteAnswerGroup(n)"
                >
                  <v-icon small>close</v-icon>
                </v-btn>
              </v-tab>
            </v-tabs>
          </v-flex>
          <v-flex shrink>
            <v-btn
              v-tooltip.top="$t('In case, user wants to random reply messages')"
              class="extra-small mt-2"
              fab
              small
              outline
              color="black"
              :disabled="alternativeGroup.length >= 5 && currentAnswerGroup >= 4"
              @click="nextAnswerGroup"
            >
              <v-icon>add</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
      </template>
      <v-layout class="mb-2 primary lighten-4">
        <v-flex>
          <v-tabs
            v-model="currentAlternative"
            :show-arrows="true"
            color="primary lighten-4"
            slider-color="primary"
            :style="{ 'max-width': maxRandomBarWidth + 'px' }"
          >
            <v-tab v-for="n in intentItems.length" :key="n" ripple>
              {{ $t('pattern') }} {{ n }}
              <v-btn
                :disabled="intentItems.length == 1"
                fab
                flat
                small
                class="mr-0"
                style="width: 25px; height: 25px"
                @click="onDeleteAlternative(n)"
              >
                <v-icon small>close</v-icon>
              </v-btn>
            </v-tab>
          </v-tabs>
        </v-flex>
        <v-flex shrink>
          <v-btn
            v-tooltip.top="$t('In case, user wants to random reply messages')"
            class="extra-small mt-2"
            fab
            small
            outline
            color="black"
            :disabled="intentItems.length >= 5 && currentAlternative >= 4"
            @click="nextAlternative"
          >
            <v-icon>add</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </template>
    <div class="mt-2"></div>
    <div>
      <template v-for="i in intentItems.length">
        <transition
          :key="`${currentAnswerGroup}_${currentAlternative}_${i}`"
          @before-enter="beforeEnter"
          @enter="enter"
          @after-enter="afterEnter"
          @before-leave="beforeLeave"
          @leave="leave"
          @after-leave="afterLeave"
        >
          <intent-group-item
            v-show="currentAlternative == i - 1"
            :key="`${currentAnswerGroup}_${currentAlternative}_${timestamp}`"
            v-model="intentItems[i - 1]"
            class="intent-group-item"
            :is-welcome="isWelcome"
            :index-alternative="i"
            :intentname="intentname"
            :available-bubbles="availableBubbles"
            :send-type="sendType"
            :is-beacon="isBeacon"
          ></intent-group-item>
        </transition>
      </template>
    </div>
    <div class="hr"></div>
    <p class="small" style="margin-top: 10px">{{ $t('Choice') }}</p>

    <div class="form-control" :class="{ 'has-error': errorTag.length > 0 }">
      <Tags
        v-model="reply"
        :tags="replies"
        style="margin-bottom: 5px"
        :btn-text="$t('Add choice')"
        @tags-changed="addReplies"
        @before-adding-tag="checkQuickReply"
      ></Tags>
      <span v-if="errorTag.length > 0" class="text-error">
        <template v-if="errorTag.includes('overmaxlength')">{{
          $t('Message cannot be longer than 20 alphabets')
        }}</template>
        <template v-else-if="errorTag.includes('overtagmax')">{{
          $t('Choices cannot be more than 10 items')
        }}</template>
        <template v-else-if="errorTag.includes('duplicate')">{{ $t('Duplicate Choice') }}</template>
      </span>
    </div>
  </dir>
</template>

<script>
import { mapGetters } from 'vuex'
import IntentGroupItem from './IntentGroupItem.vue'
import Velocity from 'velocity-animate'
import moment from 'moment'
import cloneDeep from 'lodash/cloneDeep'
import Tags from '@/components/elements/Tags'

export default {
  components: {
    IntentGroupItem,
    Tags
  },
  inject: ['$validator'],
  props: {
    value: {
      default: ''
    },
    hasAlternative: {
      default: true,
      type: Boolean
    },
    isWelcome: {
      dafault: false,
      type: Boolean
    },
    intentname: {
      default: ''
    },
    showMultipleChannel: {
      default: false
    },
    tab: {
      default: 'all'
    },
    maxIntent: {
      default: 10
    },
    hideSetting: {
      default: false
    },
    availableBubbles: {},
    sendType: {
      default: 'promotion',
      type: String
    },
    isBeacon: {
      dafault: false,
      type: Boolean
    },
    beaconQuickReplies: {}
  },
  data: function () {
    return {
      addLimit: this.maxIntent,
      group: '',
      intentItems: [],
      showEmoji: false,
      typingTimer: null,
      currentIndex: null,
      newButton: {
        title: '',
        type: 'web_url',
        url: ''
      },
      currentAlternative: 0,
      productlist: [],
      showSetting: false,
      input: {
        showMultipleChannel: false,
        randomStatus: false
      },
      timestamp: moment().format('x'),
      maxRandomBarWidth: 0,
      currentAnswerGroup: 0,
      alternativeGroup: [],
      quickReplies: [],
      errorTag: [],
      reply: '',
      currentQuickReply: ''
    }
  },
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot',
      welcomeMessage: 'welcomeMessage'
    }),

    replies: {
      get() {
        const vm = this
        if (vm.currentQuickReply && vm.currentQuickReply !== '') {
          const quick_replies = vm.currentQuickReply.split(',') || []
          const result = quick_replies.map((val) => {
            return { text: val }
          })
          return result || []
        } else {
          return []
        }
      },
      set(value) {
        const vm = this
        const result = value.map((el) => {
          return el.text
        })

        vm.updateQuickReplies(result)
      }
    }
  },
  watch: {
    alternativeGroup: {
      handler() {
        this.$emit('input', this.alternativeGroup)
      },
      deep: true
    },
    value: {
      handler() {
        this.alternativeGroup = this.value
        this.handleTransformIntentItems()
        this.handleTransformQuickReplies()
      },
      deep: true
    },
    intentItems: {
      handler() {
        this.handleUpdateIntent()
      },
      deep: true
    },
    beaconQuickReplies: {
      handler() {
        this.handleTransformQuickReplies()
      },
      deep: true
    }
  },
  mounted() {
    this.$set(this.input, 'showMultipleChannel', this.showMultipleChannel)
    if (this.intentItems.length > 1) this.$set(this.input, 'randomStatus', true)
    this.onResize()

    if (this.isBeacon) {
      this.input.randomStatus = true
      this.alternativeGroup = cloneDeep(this.value)
      this.handleTransformIntentItems()
      this.handleTransformQuickReplies()
    }
  },
  methods: {
    updateQuickReplies(payload) {
      this.quickReplies[this.currentAnswerGroup] = []
      this.quickReplies[this.currentAnswerGroup].push(payload.join(','))
      this.$emit('updateQuickReply', this.quickReplies)
    },
    handleTransformQuickReplies() {
      this.quickReplies = this.beaconQuickReplies
      const tempQuickReplies = cloneDeep(this.beaconQuickReplies)
      const tempCurrentQuickReply = tempQuickReplies[this.currentAnswerGroup]
      this.currentQuickReply = tempCurrentQuickReply.length > 0 ? tempCurrentQuickReply[0] : ''
    },
    handleTransformIntentItems() {
      const tempVal = cloneDeep(this.alternativeGroup)
      this.intentItems = []

      if (tempVal.length > 0) {
        if (tempVal[this.currentAnswerGroup].length > 0) {
          const tempIntentItems = cloneDeep(tempVal[this.currentAnswerGroup])
          this.intentItems.push(...tempIntentItems)
        }
      } else {
        this.intentItems.push([])
      }

      this.timestamp = moment().format('x')
    },
    onResize() {
      if (this.$refs.randomBar) {
        let randomBarWidth = this.$refs.randomBar.clientWidth
        let addRandomBtnWidth = 48
        this.maxRandomBarWidth = randomBarWidth - addRandomBtnWidth
      }
    },

    toggleRandomStatus() {
      this.$set(this.input, 'randomStatus', !this.input.randomStatus)
    },
    changeSetting() {
      this.$emit('changeSetting', this.input)
      this.showSetting = false
    },
    beforeEnter: function (el) {
      el.style.opacity = 0
      el.style.height = 0
    },
    enter: function (el, done) {
      setTimeout(() => {
        el.style.height = '100%'
        setTimeout(() => {
          Velocity(el, { opacity: 1, translateY: '10px' }, { complete: done, duration: 500 })
        }, 100)
      }, 600)
    },
    afterEnter: function () {
      //
    },
    beforeLeave: function (el) {
      el.style.opacity = 1
      // el.style.maxHeight = 'none'
    },
    leave: function (el, done) {
      Velocity(el, { opacity: 0, translateY: '-10px' }, { complete: done, duration: 500 })
    },
    afterLeave: function () {},
    nextAnswerGroup: function () {
      this.alternativeGroup.push([[]])
      this.currentAnswerGroup = parseInt(this.alternativeGroup.length) - 1
      this.currentAlternative = 0
      this.quickReplies.push([])
      this.currentQuickReply = ''
      this.handleResetIntentItem()
      this.timestamp = moment().format('x')
    },
    onDeleteAnswerGroup(index) {
      if (this.alternativeGroup.length > 1) {
        this.alternativeGroup.splice(index - 1, 1)
        this.currentAnswerGroup = parseInt(this.alternativeGroup.length) - 1
        this.intentItems = []
        this.intentItems = cloneDeep(this.alternativeGroup[this.currentAnswerGroup])
        this.currentAlternative = 0
        this.quickReplies.splice(index - 1, 1)
      }
      this.timestamp = moment().format('x')
    },
    nextAlternative: function () {
      this.alternativeGroup[this.currentAnswerGroup].push([])
      this.currentAlternative = parseInt(this.alternativeGroup[this.currentAnswerGroup].length) - 1
      this.intentItems = []
      this.intentItems = cloneDeep(this.alternativeGroup[this.currentAnswerGroup])
      this.timestamp = moment().format('x')
    },
    onDeleteAlternative(alternative) {
      if (this.alternativeGroup.length > 1) {
        this.alternativeGroup[this.currentAnswerGroup].splice(alternative - 1, 1)
        this.intentItems = []
        this.intentItems = cloneDeep(this.alternativeGroup[this.currentAnswerGroup])
      }
      this.timestamp = moment().format('x')
    },

    handleResetIntentItem() {
      this.intentItems = []
      this.intentItems.push([])
      this.currentAlternative = 0
    },
    handleUpdateIntent: function () {
      this.alternativeGroup[this.currentAnswerGroup] = cloneDeep(this.intentItems)
    },
    handleChangeAnswerGroup() {
      this.intentItems = this.alternativeGroup[this.currentAnswerGroup]
      this.currentAlternative = 0
      this.timestamp = moment().format('x')
      this.handleTransformQuickReplies()
    },
    addReplies: function (newTags) {
      this.replies = newTags
    },
    checkQuickReply(obj) {
      this.errorTag = []
      if (obj.tag.text.length > 20) {
        this.errorTag.push('overmaxlength')
        obj.error()
      } else if (obj.type == 'add' && this.replies.length >= 10) {
        this.errorTag.push('overtagmax')
        obj.error()
      } else if (obj.tag.isDuplicate) {
        this.errorTag.push('duplicate')
        obj.error()
      } else obj.addTag()
    }
  }
}
</script>

<style lang="scss" scoped>
.intent-group-item {
  transition: height ease 2s;
  -webkit-transition: height ease 2s;
}
</style>



// WEBPACK FOOTER //
// src/components/view/intent/IntentGroupBeacon.vue